import { flexRender } from "@tanstack/react-table";
import { AiOutlineCaretDown, AiOutlineCaretUp } from "react-icons/ai";
import { BsPinAngle, BsPinFill } from "react-icons/bs";

const Table = ({ table, className = "" }) => {
  const getCommonPinningStyles = (column) => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === "left" && column.getIsLastColumn("left");
    const isFirstRightPinnedColumn =
      isPinned === "right" && column.getIsFirstColumn("right");

    return {
      boxShadow: isLastLeftPinnedColumn
        ? "-2px 0 2px -2px gray inset"
        : isFirstRightPinnedColumn
          ? "2px 0 2px -2px gray inset"
          : undefined,
      left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
      right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? "sticky" : "relative",
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
    };
  };

  // Check if column pinning is enabled in table options
  const enableColumnPinning = table.options.enableColumnPinning ?? false;

  return (
    <table className={`table-pin-rows mt-4 table text-black ${className}`}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                className={`bg-white px-2 align-bottom text-black ${header.column.columnDef.meta?.className || ""}`}
                style={{ ...getCommonPinningStyles(header.column) }}
              >
                <div className="flex flex-row align-bottom whitespace-nowrap">
                  {header.isPlaceholder ? null : (
                    <>
                      {enableColumnPinning &&
                        header.depth === table.getHeaderGroups().length &&
                        header.column.getCanPin() && (
                          <div className="flex justify-center gap-1">
                            {header.column.getIsPinned() !== "left" ? (
                              <BsPinAngle
                                className="mx-2 mt-1 cursor-pointer"
                                onClick={() => {
                                  header.column.pin("left");
                                }}
                              />
                            ) : null}
                            {header.column.getIsPinned() ? (
                              <BsPinFill
                                className="mx-2 mt-1 cursor-pointer"
                                onClick={() => {
                                  header.column.pin(false);
                                }}
                              />
                            ) : null}
                          </div>
                        )}
                      <div
                        className={`flex flex-row ${header.column.getCanSort() ? "cursor-pointer select-none" : ""}`}
                        onClick={header.column.getToggleSortingHandler()}
                        title={
                          header.column.getCanSort()
                            ? header.column.getNextSortingOrder() === "asc"
                              ? "Sort ascending"
                              : header.column.getNextSortingOrder() === "desc"
                                ? "Sort descending"
                                : "Clear sort"
                            : undefined
                        }
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: (
                            <div className="m-1">
                              <AiOutlineCaretUp />
                            </div>
                          ),
                          desc: (
                            <div className="m-1">
                              <AiOutlineCaretDown />
                            </div>
                          ),
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    </>
                  )}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                className={`bg-white p-1 text-black ${cell.column.columnDef.meta?.className || ""}`}
                style={{ ...getCommonPinningStyles(cell.column) }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        {table.getFooterGroups().map((footerGroup) => (
          <tr key={footerGroup.id}>
            {footerGroup.headers.map((header) => (
              <th key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.footer,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </tfoot>
    </table>
  );
};

export default Table;
